import { useCallback, useEffect, useMemo, useState } from "react"

import { debounce } from "lodash"

import { useBuilderStore } from "../store"

const SAVE_TIMEOUT = 5000
const VERSIONING_TIMEOUT = 30000
const STORAGE_KEY = 'cms:post'

export default function useAutoSave() {
  const { autoSave, disableAutoSave } = useBuilderStore()

  const [saved, setSaved] = useState(true)

  const cleanLocalStorage = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY)
  }, [])

  const setLocalStorage = useCallback(data => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
  }, [])

  const getLocalStorage = useCallback(() => {
    const data = localStorage.getItem(STORAGE_KEY)
    if (!data) {
      return null
    }
    return JSON.parse(data)
  }, [])

  const saveDebounced = useMemo(() => debounce(async data => {
    try {
      await autoSave(data)
      setSaved(true)
      cleanLocalStorage()
    } catch (error) {
      return;
    }
  }, SAVE_TIMEOUT), [autoSave, cleanLocalStorage])

  const versioningDebounced = useMemo(() => debounce(async data => {
    try {
      await autoSave({ ...data, shouldAudit: true })
    } catch (error) {
      return;
    }
  }, VERSIONING_TIMEOUT), [autoSave])

  const handleAutoSave = useCallback(async data => {
    if (disableAutoSave) return;
    setLocalStorage(data)
    setSaved(false)
    saveDebounced(data)
    versioningDebounced(data)
  }, [saveDebounced, versioningDebounced, setLocalStorage, disableAutoSave])

  useEffect(() => () => {
    saveDebounced.cancel()
    versioningDebounced.cancel()
  }, [saveDebounced, versioningDebounced])

  return useMemo(() => ({
    handleAutoSave,
    saved,
    restoreUnsaved: () => getLocalStorage(),
  }), [handleAutoSave, getLocalStorage, saved])
}

